<template>
  <v-dialog
    v-model="dialog"
    :max-width="calcMaxWidth"
  >
    <template #activator="{ on, attrs }">
      <ActivatorButton
        :parent-attrs="attrs"
        :parent-on="on"
        :icon-or-title="buttonTitle"
        :tooltip-text="title"
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        @activate="onButtonActivate"
      />
    </template>

    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
    >
      <v-card>
        <v-card-title>
          <v-row>
            <v-col>
              {{ !!item ? 'Edit' : 'Create' }}
            </v-col>
            <v-col>
              <v-select
                v-model="cls"
                :items="classes"
                label="Class"
                :rules="[(v) => !!v || 'Required']"
              />
            </v-col>
            <v-col v-if="!['REFERENCE', 'GROUP', 'WORKFLOW'].includes(cls)">
              <v-select
                v-model="type"
                :items="classOperationTypes[cls]"
                label="Type"
                :rules="[(v) => !!v || 'Required']"
              />
            </v-col>
            <v-col v-if="!global">
              <v-text-field
                v-model="editItem.flag"
                label="Flag"
              />
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row
              v-if="['SMIRKS', 'SMILES', 'SMARTS'].includes(type)"
              dense
            >
              <v-col>
                <KetcherEditor
                  v-model="rxn"
                  resolve
                  lookup
                  @found="setFound"
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col v-if="type === 'METHOD' || cls === 'REFERENCE'">
                <v-select
                  v-model="editItem.value"
                  :items="operations"
                  label="Operation"
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col v-else-if="!['SMIRKS', 'SMILES', 'SMARTS'].includes(type)">
                <v-text-field
                  v-model="editItem.value"
                  label="Name"
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>

              <v-col v-if="!['METHOD', 'REFERENCE'].includes(type)">
                <v-text-field
                  v-model="editItem.text"
                  label="Title"
                />
              </v-col>

              <v-col v-if="cls === 'CHECK'">
                <v-select
                  v-model="editItem.severity"
                  :items="['INFO', 'WARNING', 'ERROR']"
                  label="Severity"
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>

              <v-col v-if="cls === 'CHECK'">
                <v-select
                  v-model="editItem.action"
                  :items="['CONTINUE', 'SKIP', 'STOP']"
                  label="Action"
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editItem.description"
                  label="Description"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <span
            v-if="message"
            style="color: red; font-size: larger"
          >
            {{ message }}
          </span>
          <v-spacer />
          <v-btn
            @click="dialog = false"
            aria-label="Cancel"
          >
            Cancel
          </v-btn>
          <v-btn
            @click="onSave"
            :disabled="!changed"
            aria-label="Save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import KetcherEditor from '@/components/ketcher-editor/KetcherEditor.vue'
import DialogBase from '@/components/dialogs/DialogBase.vue'
import { SERVICES_URL, getErrorMessage, STDIZER_URL } from '@/main'
import ActivatorButton from '@/components/ActivatorButton.vue'

export default {
  components: { KetcherEditor, ActivatorButton },

  mixins: [DialogBase],

  props: {
    item: {
      type: Object,
      required: false,
    },
    global: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: true,
      rxn: null,
      changed: false,
      cls: null,
      type: null,
      editItem: {
        cls: null,
        type: null,
        flag: null,
        text: null,
        description: null,
        value: null,
        severity: null,
        action: null,
      },
      classOperationTypes: {
        CHANGE: ['METHOD', 'SMIRKS'],
        CHECK: ['METHOD', 'SMILES', 'SMARTS'],
      },
      availableOperations: null,
    }
  },

  computed: {
    groupId() {
      return this.$route.params.id
    },
    STDIZER_URL() {
      return STDIZER_URL
    },
    calcMaxWidth() {
      return ['SMIRKS', 'SMILES', 'SMARTS'].includes(this.type) ? '900' : this.maxWidth
    },
    operations() {
      let query = this.cls === 'REFERENCE' ? 'GROUP' : this.cls
      return this.availableOperations.filter((o) => o['@c'].toUpperCase().endsWith(query))
    },
    classes() {
      return this.global ? ['GROUP', 'WORKFLOW'] : ['CHANGE', 'CHECK', 'REFERENCE']
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.message = null
        if (!this.item) {
          this.editItem = {
            type: null,
            flag: null,
            text: null,
            description: null,
            value: null,
            severity: null,
            action: null,
          }
          this.cls = this.global ? 'GROUP' : 'CHANGE'
        } else {
          this.editItem = Object.assign({}, this.item)
          if (this.editItem['@c'] === '.Change') this.cls = 'CHANGE'
          else if (this.editItem['@c'] === '.Check') this.cls = 'CHECK'
          else if (this.editItem['@c'] === '.GroupReference') this.cls = 'REFERENCE'
          else if (this.editItem['@c'] === '.LibraryGroup') this.cls = 'GROUP'
          else if (this.editItem['@c'] === '.Workflow') this.cls = 'WORKFLOW'

          this.type = this.editItem.type

          if (['SMIRKS', 'SMILES', 'SMARTS'].includes(this.type)) {
            if (this.editItem.value.includes('\n')) {
              this.rxn = this.editItem.value
              this.changed = false
            } else if (this.type === 'SMIRKS') {
              this.$axios
                .get(`${SERVICES_URL}/smirks2rxn?smirks=${encodeURIComponent(this.editItem.value)}`)
                .then((response) => {
                  this.rxn = response.data
                  this.changed = false
                })
            } else {
              this.$axios
                .get(`${SERVICES_URL}/layout?smiles=${encodeURIComponent(this.editItem.value)}`)
                .then((response) => {
                  this.rxn = response.data
                  this.changed = false
                })
            }
          }
        }
      }
    },
    rxn() {
      this.changed = true
      this.editItem.value = this.rxn
    },
    editItem() {
      this.changed = true
    },
  },

  methods: {
    setFound(record) {
      this.editItem.text = record.name
    },
    onSave() {
      if (this.changed && this.$refs.form.validate()) {
        if (this.cls === 'CHECK') {
          this.editItem['@c'] = '.Check'
          this.editItem.type = this.type
        } else if (this.cls === 'CHANGE') {
          this.editItem['@c'] = '.Change'
          this.editItem.type = this.type
        } else if (this.cls === 'REFERENCE') {
          this.editItem['@c'] = '.GroupReference'
          this.editItem.type = 'REFERENCE'
        } else if (this.cls === 'GROUP') {
          this.editItem['@c'] = '.LibraryGroup'
          this.editItem.type = 'GROUP'
        } else if (this.cls === 'WORKFLOW') {
          this.editItem['@c'] = '.Workflow'
          this.editItem.type = 'GROUP'
        }

        if (['SMIRKS'].includes(this.editItem.type)) {
          this.$axios
            .post(`${SERVICES_URL}/convert`, {
              content: this.rxn,
              type: 'rxn2smirks',
            })
            .then((response) => {
              this.editItem.value = response.data
              this.$emit('onSave', this.editItem)
              this.dialog = false
            })
            .catch((error) => {
              this.uploading = false
              this.message = getErrorMessage(error)
            })
        } else if (['SMILES', 'SMARTS'].includes(this.editItem.type)) {
          this.$axios
            .post(`${SERVICES_URL}/convert`, {
              content: this.rxn,
              type: 'mol2smiles',
            })
            .then((response) => {
              this.editItem.value = response.data
              this.$emit('onSave', this.editItem)
              this.dialog = false
            })
            .catch((error) => {
              this.uploading = false
              this.message = getErrorMessage(error)
            })
        } else {
          if (this.type === 'METHOD' || this.cls === 'REFERENCE')
            this.editItem.text = this.operations.find((o) => o.value === this.editItem.value).text
          this.$emit('onSave', this.editItem)
          this.dialog = false
        }
      }
    },
    getData() {
      this.$axios.get(`${STDIZER_URL}/operations`).then((response) => {
        this.availableOperations = response.data
      })
    },
  },

  mounted() {
    this.getData()
  },
}
</script>
